// Harmonic-mixing helpers for the Set Planner's local-library fallback.
// Local tracks store `key` as musical notation ("Fmajor" / "Fminor", sometimes
// "F" / "Fm" after formatKey, occasionally already-Camelot like "8A"). We map
// to the Camelot wheel so we can score key compatibility.

const MAJOR_TO_CAMELOT = {
    'B': '1B', 'F#': '2B', 'Gb': '2B', 'Db': '3B', 'C#': '3B', 'Ab': '4B', 'G#': '4B',
    'Eb': '5B', 'D#': '5B', 'Bb': '6B', 'A#': '6B', 'F': '7B', 'C': '8B', 'G': '9B',
    'D': '10B', 'A': '11B', 'E': '12B',
};
const MINOR_TO_CAMELOT = {
    'Ab': '1A', 'G#': '1A', 'Eb': '2A', 'D#': '2A', 'Bb': '3A', 'A#': '3A', 'F': '4A',
    'C': '5A', 'G': '6A', 'D': '7A', 'A': '8A', 'E': '9A', 'B': '10A', 'F#': '11A',
    'Gb': '11A', 'Db': '12A', 'C#': '12A',
};

// Parse any of the stored key formats to a Camelot code like "8A"/"11B", or
// null when unknown/unparseable.
export function parseKeyToCamelot(raw) {
    if (!raw || typeof raw !== 'string') return null;
    let s = raw.trim();
    if (!s || s.toUpperCase() === 'N/A') return null;

    // Already Camelot, e.g. "8A", "11 b".
    const cam = s.match(/^(\d{1,2})\s*([AB])$/i);
    if (cam) {
        const n = parseInt(cam[1], 10);
        if (n >= 1 && n <= 12) return n + cam[2].toUpperCase();
    }

    // Open Key (Traktor), e.g. "9m" (minor) / "9d" (major). Same wheel as
    // Camelot rotated by 7: Open 1m = A minor = Camelot 8A.
    const open = s.match(/^(\d{1,2})\s*([dm])$/i);
    if (open) {
        const n = parseInt(open[1], 10);
        if (n >= 1 && n <= 12) {
            return (((n + 6) % 12) + 1) + (open[2].toLowerCase() === 'm' ? 'A' : 'B');
        }
    }

    s = s.toLowerCase();
    let mode;
    if (s.includes('minor') || s.includes('min')) mode = 'minor';
    else if (s.includes('major') || s.includes('maj')) mode = 'major';
    // "fm", "c#m", "abm" — trailing m that isn't just a bare note.
    else if (/m$/.test(s) && !/^[a-g][#b]?$/.test(s)) mode = 'minor';
    else mode = 'major';

    const nm = s.match(/^([a-g])([#b]?)/);
    if (!nm) return null;
    const note = nm[1].toUpperCase() + (nm[2] === '#' ? '#' : nm[2] === 'b' ? 'b' : '');
    const map = mode === 'minor' ? MINOR_TO_CAMELOT : MAJOR_TO_CAMELOT;
    return map[note] || null;
}

function splitCamelot(code) {
    if (!code) return null;
    const m = code.match(/^(\d{1,2})([AB])$/);
    if (!m) return null;
    return { num: parseInt(m[1], 10), letter: m[2] };
}

// 2 = same key, 1 = harmonically adjacent (±1 on the wheel, or relative
// major/minor), 0 = clashing or unknown. Unknown keys score 0 (neutral) so
// they don't outrank a real match but aren't excluded.
export function keyCompatScore(keyA, keyB) {
    const a = splitCamelot(parseKeyToCamelot(keyA));
    const b = splitCamelot(parseKeyToCamelot(keyB));
    if (!a || !b) return 0;
    if (a.num === b.num && a.letter === b.letter) return 2;
    if (a.num === b.num) return 1;                       // relative major/minor
    const diff = Math.abs(a.num - b.num);
    const wrap = Math.min(diff, 12 - diff);
    if (wrap === 1 && a.letter === b.letter) return 1;   // adjacent, same mode
    return 0;
}

// Camelot-wheel colour per number (1–12) — A and B of the same number share a
// colour. Exact HEX values (standard Camelot palette).
const CAMELOT_HEX = {
    1: '#8CFFD2', 2: '#A1FF9A', 3: '#B7F473', 4: '#D3D173', 5: '#F1A278', 6: '#FF7F8E',
    7: '#F07EB1', 8: '#D17EDD', 9: '#B47EFE', 10: '#9EA3FF', 11: '#8BD8FF', 12: '#7EFFF8',
};

// Background colour for a key, following the Camelot wheel. Returns null for
// unknown keys (caller falls back to the default pill colour).
export function camelotColor(key) {
    const cam = parseKeyToCamelot(key);
    const m = cam && cam.match(/^(\d{1,2})([AB])$/);
    if (!m) return null;
    return CAMELOT_HEX[parseInt(m[1], 10)] || null;
}

// Preferred musical name per Camelot code (flats where customary).
const CAMELOT_TO_MUSICAL = {
    '1A': 'Abm', '2A': 'Ebm', '3A': 'Bbm', '4A': 'Fm', '5A': 'Cm', '6A': 'Gm',
    '7A': 'Dm', '8A': 'Am', '9A': 'Em', '10A': 'Bm', '11A': 'F#m', '12A': 'Dbm',
    '1B': 'B', '2B': 'F#', '3B': 'Db', '4B': 'Ab', '5B': 'Eb', '6B': 'Bb',
    '7B': 'F', '8B': 'C', '9B': 'G', '10B': 'D', '11B': 'A', '12B': 'E',
};

// Musical notation ("Fmajor" → "F", "Fminor" → "Fm"); wheel notations
// (Camelot "8A" / Open Key "9m") are converted via the Camelot mapping.
const musicalKey = (key) => {
    if (/^\d/.test(key.trim())) {
        const cam = parseKeyToCamelot(key);
        if (cam && CAMELOT_TO_MUSICAL[cam]) return CAMELOT_TO_MUSICAL[cam];
    }
    return key.replace(/major/i, '').replace(/minor/i, 'm').trim();
};

// Format a stored key for display in the chosen notation:
//   'camelot'  → "8A" / "11B"
//   'musical'  → "Am" / "C"  (default)
export function formatKeyDisplay(key, notation = 'musical') {
    if (!key || key === 'N/A') return key || '';
    if (notation === 'camelot') return parseKeyToCamelot(key) || musicalKey(key);
    return musicalKey(key);
}

// Fractional BPM difference (0 = identical). Handles half/double-time so a
// 140 track is treated as close to a 70 track.
export function bpmDiffRatio(a, b) {
    const x = Number(a), y = Number(b);
    if (!x || !y) return Infinity;
    const candidates = [y, y * 2, y / 2];
    return Math.min(...candidates.map((c) => Math.abs(x - c) / x));
}
